import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { paymentAPI } from '@/services/api';
import { CreditCard, ShieldCheck, ArrowLeft, Crown, Image, Film } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentCheckoutPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const [loading, setLoading] = useState(false);
	const orderData = location.state;

	useEffect(() => {
		if (!user) {
			navigate('/auth');
			return;
		}
		if (!orderData) {
			navigate('/dashboard');
			return;
		}
	}, [user, orderData, navigate]);

	if (!orderData) return null;

	const { lineItems, total, adType, adId, vat } = orderData;

	const handleStripePayment = async () => {

		setLoading(true);
		try {
			const res = await paymentAPI.checkout({
				checkout_type: 'ad_order',
				ad_id: adId,
				ad_type: adType,
				line_items: lineItems,
				origin_url: window.location.origin,
			});
			window.location.href = res.data.url;
		} catch (err) {
			toast.error(err.response?.data?.detail || t('payment.failed'));
			setLoading(false);
		}
	};

	const getIcon = (type) => {
		if (type === 'featured_ad') return <Crown className="w-4 h-4 text-[#8B4513]" />;
		if (type === 'extra_images') return <Image className="w-4 h-4 text-[#8B4513]" />;
		if (type === 'extra_videos') return <Film className="w-4 h-4 text-[#8B4513]" />;
		return <CreditCard className="w-4 h-4 text-[#8B4513]" />;
	};
	const vatDivisor = 1 + (vat / 100);
	const subTotal = (total / vatDivisor).toFixed(2);
	const vatAmount = (total - subTotal).toFixed(2);

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#FAF8F4]" data-testid="payment-checkout-page">
			<div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				<button onClick={() => navigate(-1)}
								className="flex items-center gap-2 text-sm text-[#666055] hover:text-[#1A3C34] mb-6 transition-colors"
								data-testid="back-button">
					<ArrowLeft className="w-4 h-4" /> {t('payment.back_to_ad')}
				</button>

				<h1 className="text-3xl font-medium text-[#1A3C34] font-['Playfair_Display'] mb-8"
						data-testid="checkout-title">
					{t('payment.checkout_title')}
				</h1>

				{/* Order Summary */}
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 mb-6">
					<h2 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-4">
						{t('payment.order_summary')}
					</h2>
					<div className="space-y-3">
						{lineItems.map((item, i) => (
							<div key={i} className="flex items-center justify-between py-2 border-b border-[#F2F0E9] last:border-0"
									 data-testid={`line-item-${i}`}>
								<div className="flex items-center gap-3">
									{getIcon(item.type)}
									<p className="text-sm font-medium text-[#1A3C34]">{item.label}</p>
								</div>
								<span className="text-sm font-bold text-[#8B4513]">{item.quantity} x</span>
							</div>
						))}
						<div className="flex items-center justify-between py-2 border-b border-[#F2F0E9] last:border-0"
								 data-testid={`line-item-subtotal`}>
							<div className="flex items-center gap-3">
								<p className="text-sm font-medium text-[#1A3C34]">{t('payment.subtotal')}</p>
							</div>
							<span className="text-sm font-bold text-[#8B4513]">{subTotal} EUR</span>
						</div>
						<div className="flex items-center justify-between py-2 border-b border-[#F2F0E9] last:border-0"
								 data-testid={`line-item-vat`}>
							<div className="flex items-center gap-3">
								<p className="text-sm font-medium text-[#1A3C34]">{t('admin.vat_percent')}</p>
							</div>
							<span className="text-sm font-bold text-[#8B4513]">{vatAmount} EUR</span>
						</div>
					</div>
					<div className="mt-4 pt-4 border-t border-[#E6E2D6] flex justify-between items-center">
						<span className="text-base font-bold text-[#1A3C34]">{t('payment.total')}</span>
						<span className="text-xl font-bold text-[#8B4513]" data-testid="checkout-total">{total.toFixed(2)} EUR</span>
					</div>
				</div>

				{/* Payment Methods */}
				<div className="bg-white border border-[#E6E2D6] rounded-sm p-6 mb-6">
					<h2 className="text-lg font-semibold text-[#1A3C34] font-['Playfair_Display'] mb-4">{t('payment.select_method')}</h2>
					<div className="space-y-3">
						<button
							onClick={handleStripePayment}
							disabled={loading}
							className="w-full flex items-center gap-4 p-4 border-2 border-[#1A3C34] rounded-sm bg-[#F9F7F2] hover:bg-[#F2F0E9] transition-colors text-left"
							data-testid="pay-stripe"
						>
							<div className="w-12 h-12 bg-[#1A3C34] rounded-sm flex items-center justify-center flex-shrink-0">
								<CreditCard className="w-6 h-6 text-white" />
							</div>
							<div className="flex-1">
								<p className="font-semibold text-[#1A3C34]">{t('payment.stripe_title')}</p>
								<p className="text-xs text-[#666055]">{t('payment.stripe_desc')}</p>
							</div>
							{loading && <div className="w-5 h-5 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />}
						</button>

						{/* MB WAY placeholder */}
						<div className="w-full flex items-center gap-4 p-4 border border-[#E6E2D6] rounded-sm bg-[#F9F7F2] opacity-50 cursor-not-allowed"
								 data-testid="pay-mbway-disabled">
							<div className="w-12 h-12 bg-[#E6E2D6] rounded-sm flex items-center justify-center flex-shrink-0">
								<span className="text-xs font-bold text-[#666055]">MB</span>
							</div>
							<div className="flex-1">
								<p className="font-semibold text-[#666055]">MB WAY</p>
								<p className="text-xs text-[#666055]">{t('payment.coming_soon')}</p>
							</div>
						</div>
					</div>
				</div>

				{/* Trust badges */}
				<div className="flex items-center justify-center gap-6 text-xs text-[#666055]">
					<div className="flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> {t('payment.secure')}</div>
					<div className="flex items-center gap-1"><CreditCard className="w-4 h-4" /> {t('payment.encrypted')}</div>
				</div>
			</div>
		</div>
	);
}